import React, { useState } from 'react';
import toast from 'react-hot-toast';
import type { Coach } from '@court-sync/shared';
import { TierFeatureKey } from '@court-sync/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
} from '@/components/ui';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';
import { useSubscriptionCheck } from '@/hooks/useSubscriptionCheck';
import { coachService } from '@/services/coach';

export interface SendCoachInviteDialogProps {
  coach: Coach | null;
  isOpen: boolean;
  onClose: () => void;
  academyId: string;
  onSent?: () => void;
}

export function SendCoachInviteDialog({
  coach,
  isOpen,
  onClose,
  academyId,
  onSent,
}: SendCoachInviteDialogProps) {
  const { hasValidSubscription, tooltipMessage } = useSubscriptionCheck();
  const { hasAccess: canSendCoachEmailInvites } = useManagementDashboardFeatureAccess(
    TierFeatureKey.COACH_INVITES
  );
  const [sendingInvite, setSendingInvite] = useState(false);

  const handleConfirmInvite = async () => {
    if (!coach || !academyId) return;

    try {
      setSendingInvite(true);
      await coachService.sendInvite(academyId, coach.email, coach.id);
      toast.success(`Invitation sent to ${coach.email}`);
      onSent?.();
      onClose();
    } catch (error: unknown) {
      console.error('Error inviting coach:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  return (
    <Dialog open={isOpen && !!coach} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Coach Invitation</DialogTitle>
          <DialogDescription>
            {coach && (
              <>
                Would you like to send an invite email to{' '}
                <strong>
                  {coach.firstName} {coach.lastName}
                </strong>{' '}
                ({coach.email}) to create a coach profile for your academy?
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
            <Button
              onClick={() => void handleConfirmInvite()}
              disabled={sendingInvite || !hasValidSubscription || !canSendCoachEmailInvites}
            >
              {sendingInvite ? 'Sending...' : 'Send Invitation'}
            </Button>
          </Tooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
